import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';

import {
  ExtendedBox,
  ExtendedFlex,
  Formatters,
  Heading,
  StyledTable,
  Body,
  TooltipIcon,
  Tooltip,
} from '../../coreui';

import DashboardSection from './DashboardSection';
import DashboardTableCell from './DashboardTableCell';
import HeaderComponent from './TableHeader';

const columns = (investments) => {
  const sumInvestmentAmount = investments.reduce(
    (accum, item) => (accum + item.totalInvestmentAmount), 0.00,
  );
  const sumTotalReturnedAmount = investments.reduce(
    (accum, item) => (accum + item.totalDistributionsPaid), 0.00,
  );

  return [
    {
      Header: <HeaderComponent>Investment</HeaderComponent>,
      accessor: 'title',
      minWidth: 300,
      Cell: DashboardTableCell(),
    },
    {
      Header: <HeaderComponent>Type</HeaderComponent>,
      accessor: 'type',
      Cell: DashboardTableCell(),
    },
    {
      Header: <HeaderComponent>Close of Escrow Date</HeaderComponent>,
      accessor: 'escrowCloseDate',
      id: 'closeOfEscrowDate',
      Cell: DashboardTableCell(),
    },
    {
      Header: <HeaderComponent>Maturity Date</HeaderComponent>,
      accessor: 'maturityDate',
      id: 'date',
      Cell: DashboardTableCell(),
    },
    {
      Header: <HeaderComponent>Invested Amount</HeaderComponent>,
      accessor: 'totalInvestmentAmount',
      Cell: DashboardTableCell(Formatters.DOLLARS_DECIMALS),
      id: 'amount',
      ...(investments.length === 0 ? {} : {
        Footer: () => {
          if (investments.length === 0) {
            return null;
          }
          return (
            <HeaderComponent>
              {numeral(sumInvestmentAmount).format(Formatters.DOLLARS_DECIMALS)}
            </HeaderComponent>
          );
        },
      }),
    },
    {
      Header: <HeaderComponent>Total Returned</HeaderComponent>,
      accessor: 'totalDistributionsPaid',
      Cell: DashboardTableCell(Formatters.DOLLARS_DECIMALS),
      id: 'totalReturned',
      ...(investments.length === 0 ? {} : {
        Footer: () => {
          if (investments.length === 0) {
            return null;
          }
          return (
            <HeaderComponent>
              {numeral(sumTotalReturnedAmount).format(Formatters.DOLLARS_DECIMALS)}
            </HeaderComponent>
          );
        },
      }),
    },
    {
      Header: (
        <ExtendedFlex
          align="center"
        >
          <HeaderComponent>Actual IRR/Return</HeaderComponent>
          <TooltipIcon
            tooltipId="completedInvestmentsTableReturn"
            ml={5}
            onClick={(e) => (
              // Stop event propagation. Otherwise, the table will sort when
              // the tooltip icon is clicked or tapped.
              e.stopPropagation()
            )}
          />
          <Tooltip id="completedInvestmentsTableReturn">
            <Body type="B5">
              {`The return figure represents the offering's annualized return.
              Your investment's annualized return may be slightly higher or
              lower depending on the timing of your investment and other factors.`}
            </Body>
          </Tooltip>
          {/*
            We need an empty div here for the sort direction pseudo selector.
            Without it, the direction arrow will appear on the tooltip.
           */}
          <div />
        </ExtendedFlex>
      ),
      accessor: 'annualizedReturn',
      Cell: DashboardTableCell(Formatters.PERCENTAGE2),
      id: 'annualizedReturn',
      minWidth: 170,
    },
  ];
};

const CompletedInvestmentsTable = ({
  investments,
}) => {
  if (!investments) {
    return null;
  }

  return (
    <DashboardSection
      title="Private Placements"
      contentWidth="100%"
    >
      <ExtendedFlex
        flexDirection="column"
        width={1}
        alignItems="left"
        my={[30, 80]}
      >
        <ExtendedBox
          w={['auto', 'auto', 'auto', 'auto', 1]}
          mx={[-50, -50, -50, 0, 0]}
        >
          <ExtendedFlex
            mb="20px"
            justifyContent="flex-start"
            width={[1, 1, 1, '92%']}
            px={[50, 50, 50, 0, 0]}
            mx="auto"
          >
            <Heading
              mt={0}
              type="H9"
            >
              Completed Investments
            </Heading>
          </ExtendedFlex>
          <ExtendedBox
            overflowX="scroll"
            width="auto"
          >
            <StyledTable
              data={investments}
              columns={columns(investments)}
              sortable
              showPagination={false}
              defaultPageSize={investments.length}
              width="100%"
              minRows={0}
              defaultSorted={[{ id: 'date', desc: true }]}
              className="-striping -highlighting"
              NoDataComponent={() => { return null; }}
            />
          </ExtendedBox>
          {
            (investments.length === 0) &&
            <ExtendedFlex
              w="calc(92% - 30px)"
              px={[0, 50, 50, 0, 0]}
              mx="auto"
              flexDirection="column"
              align="flex-start"
            >
              <Body
                type="B1"
              >
                You have no completed items.
              </Body>
            </ExtendedFlex>
          }
        </ExtendedBox>
      </ExtendedFlex>
    </DashboardSection>
  );
};

CompletedInvestmentsTable.propTypes = {
  investments: PropTypes.array,
};

export default CompletedInvestmentsTable;
